import { Component, EventEmitter, Input, Output, ViewChild, ElementRef } from '@angular/core';
import { FingerMode, GestureMode, RecordedStep } from '../../Models/recorded-action-model';
import { RestService } from '../../Services/rest.service';
import { Literals } from '../../literals';

const STEP_NAME_KEYWORDS = [
  'open', 'openWindow', 'pause', 'selectFrame', 'selectWindow', 'close', 'ajaxWait', 'domWait',
  'type', 'select', 'submit', 'sendKeys', 'storeText', 'storeValue', 'verifySelectedValue',
  'assertText', 'verifyText', 'click', 'refresh', 'break', 'contextMenu', 'contextMenuAt',
  'doubleClick', 'doubleClickAt', 'dragAndDrop', 'dragAndDropToObject', 'waitForElementPresent',
  'waitForSomethingSelected', 'waitForVisible', 'verifyChecked', 'verifyEditable',
  'verifyElementNotPresent', 'verifyElementPresent', 'verifyNotEditable', 'mouseMoveAt',
  'mouseMove', 'mouseOver', 'assertAlert', 'chooseOkOnNextConfirmation', 'assertPrompt',
  'assertConfirmation', 'chooseCancelOnNextConfirmation', 'answerOnNextPrompt',
  'abstract', 'continue', 'for', 'new', 'switch', 'assert', 'default', 'goto*', 'package',
  'synchronized', 'boolean', 'do', 'if', 'private', 'this', 'double', 'implements',
  'protected', 'throw', 'byte', 'else', 'import', 'public', 'throws', 'case', 'enum*',
  'instanceof', 'return', 'transient', 'catch', 'extends', 'int', 'short', 'try', 'char',
  'final', 'interface', 'static', 'void', 'class', 'finally', 'long', 'strictfp**',
  'volatile', 'const*', 'float', 'native', 'super', 'while', 'Edit ', 'Save As '
];

@Component({
  selector: 'ze-gesture-controls',
  templateUrl: './gesture-controls.component.html',
  styleUrls: ['./gesture-controls.component.scss']
})
export class GestureControlsComponent {

  @ViewChild('stepNameInput', { static: false }) stepNameInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('typeInputRef', { static: false }) typeInputRef!: ElementRef<HTMLInputElement>;

  @Input() fingerMode: FingerMode = '1';
  @Input() selectedMode: GestureMode = Literals.TAP;
  @Input() isRecording = false;
  @Input() existingSteps: RecordedStep[] = [];

  @Output() fingerModeChanged = new EventEmitter<FingerMode>();
  @Output() modeChanged = new EventEmitter<GestureMode>();
  @Output() recordingStarted = new EventEmitter<string>();
  @Output() recordingResumed = new EventEmitter<string>();
  @Output() recordingPaused = new EventEmitter<void>();
  @Output() recordingStopped = new EventEmitter<void>();
  @Output() newStepStarted = new EventEmitter<string>();
  @Output() modalOpenChange = new EventEmitter<boolean>();
  @Output() typeConfirmed = new EventEmitter<{ coords: { x: number; y: number; realX: number; realY: number }; element: any; text: string }>();
  @Output() typeCancelled = new EventEmitter<void>();
  @Output() clearAndTypeConfirmed = new EventEmitter<{ coords: { x: number; y: number; realX: number; realY: number }; element: any; text: string }>();

  readonly twoFingerModes: GestureMode[] = [Literals.ZOOM_IN, Literals.ZOOM_OUT, Literals.TWO_FINGER_SWIPE];
  readonly threeFingerModes: GestureMode[] = [Literals.THREE_FINGER_SWIPE];
  readonly typeMode: GestureMode = Literals.TYPE;

  // ── Modal state ───────────────────────────────────────────────────────────────
  showStepModal = false;
  stepNameValue = '';
  currentStepName = '';
  modalMode: 'new-first' | 'new-step' | 'continue' = 'new-first';

  // ── Validation state ──────────────────────────────────────────────────────────
  stepNameError: 'specialCharacters' | 'keyword' | 'duplicate' | null = null;
  isLoadingStepNames = false;
  stepNamesLoadError = false;
  private dbStepNames: string[] = [];

  // ── Pause / type state ────────────────────────────────────────────────────────
  isPaused = false;
  showTypePanel = false;
  typeValue = '';
  pendingTypeCoords: { x: number; y: number; realX: number; realY: number } | null = null;
  pendingTypeElement: any | null = null;

  constructor(private restService: RestService) { }

  // ── DB step names ─────────────────────────────────────────────────────────────

  loadDbStepNames(): void {
    const projectId = localStorage.getItem('projectIdDb2');
    if (!projectId) {
      this.dbStepNames = [];
      return;
    }

    this.isLoadingStepNames = true;
    this.stepNamesLoadError = false;

    this.restService.get('projectStepsdb2', projectId).subscribe(
      (response: any) => {
        const steps: any[] = Array.isArray(response) ? response
          : Array.isArray(response.data) ? response.data
            : [];

        this.dbStepNames = steps
          .map((s: any) => s.name || s.stepName || '')
          .filter(Boolean);

        this.isLoadingStepNames = false;
        if (this.stepNameValue) {
          this.validateStepName(this.stepNameValue);
        }
      },
      (_err: any) => {
        this.isLoadingStepNames = false;
        this.stepNamesLoadError = true;
      }
    );
  }

  // ── Validation ────────────────────────────────────────────────────────────────

  validateStepName(value: string): void {
    this.stepNameError = null;
    if (!value) return;

    const trimmedValue = value.trim();
    const trimmedCurrent = this.currentStepName.trim();

    // Rule 1: format — must start with letter or underscore, only letters/digits/_/-/space after
    if (!/^[A-Za-z_][A-Za-z0-9_\- ]*$/.test(trimmedValue)) {
      this.stepNameError = 'specialCharacters';
      return;
    }

    // Rule 2: reserved keyword
    if (STEP_NAME_KEYWORDS.some(keyword => keyword.trim() === trimmedValue)) {
      this.stepNameError = 'keyword';
      return;
    }

    // Rule 3: local duplicate (skip the step being actively continued) — case-sensitive
    const localDuplicate = this.existingSteps.some(
      s => s.name.trim() === trimmedValue && s.name.trim() !== trimmedCurrent
    );
    if (localDuplicate) {
      this.stepNameError = 'duplicate';
      return;
    }

    // Rule 4: DB duplicate — case-sensitive
    const dbDuplicate = this.dbStepNames.some(
      name => name.trim() === trimmedValue && name.trim() !== trimmedCurrent
    );
    if (dbDuplicate) {
      this.stepNameError = 'duplicate';
    }
  }

  get stepNameErrorMessage(): string {
    switch (this.stepNameError) {
      case 'specialCharacters':
        return 'Must start with a letter or underscore, and contain only letters, digits, _, -, or spaces.';
      case 'keyword':
        return 'This name is a reserved keyword and cannot be used.';
      case 'duplicate':
        return 'A step with this name already exists.';
      default:
        return '';
    }
  }

  get isStepNameValid(): boolean {
    return (
      !!this.stepNameValue.trim() &&
      this.stepNameError === null
    );
  }

  // ── Type panel ────────────────────────────────────────────────────────────────

  openTypePanel(event: { coords: { x: number; y: number; realX: number; realY: number }; element: any }) {
    this.pendingTypeCoords = event.coords;
    this.pendingTypeElement = event.element;
    this.typeValue = '';
    this.showTypePanel = true;
    setTimeout(() => this.typeInputRef.nativeElement.focus(), 100);
  }

  confirmType() {
    if (!this.typeValue.trim() || !this.pendingTypeCoords) return;
    this.typeConfirmed.emit({ coords: this.pendingTypeCoords, element: this.pendingTypeElement, text: this.typeValue });
    this.showTypePanel = false;
    this.typeValue = '';
    this.pendingTypeCoords = null;
    this.pendingTypeElement = null;
    this.selectedMode = Literals.TAP;
    this.modeChanged.emit(Literals.TAP);
  }

  cancelType() {
    this.typeCancelled.emit();
    this.showTypePanel = false;
    this.typeValue = '';
    this.pendingTypeCoords = null;
    this.pendingTypeElement = null;
    if (this.selectedMode === Literals.TYPE) {
      this.selectedMode = Literals.TAP;
      this.modeChanged.emit(Literals.TAP);
    }
  }

  confirmClearAndType() {
    if (!this.typeValue.trim() || !this.pendingTypeCoords) return;
    this.clearAndTypeConfirmed.emit({ coords: this.pendingTypeCoords, element: this.pendingTypeElement, text: this.typeValue });
    this.showTypePanel = false;
    this.typeValue = '';
    this.pendingTypeCoords = null;
    this.pendingTypeElement = null;
    this.selectedMode = Literals.TAP;
    this.modeChanged.emit(Literals.TAP);
  }

  // ── Finger / gesture ──────────────────────────────────────────────────────────

  switchFingerMode(mode: FingerMode) {
    this.fingerMode = mode;
    if (mode === '1') this.selectedMode = Literals.TAP;
    else if (mode === '2') this.selectedMode = Literals.ZOOM_IN;
    else if (mode === '3') this.selectedMode = Literals.THREE_FINGER_SWIPE;
    this.fingerModeChanged.emit(this.fingerMode);
    this.modeChanged.emit(this.selectedMode);
  }

  selectMode(mode: GestureMode) {
    if (mode === Literals.TYPE && this.selectedMode === Literals.TYPE) {
      this.selectedMode = Literals.TAP;
      this.modeChanged.emit(Literals.TAP);
      return;
    }

    this.selectedMode = mode;
    this.modeChanged.emit(mode);
  }

  // ── Modal helpers ─────────────────────────────────────────────────────────────

  promptStartRecording() {
    this.modalMode = 'new-first';
    this.stepNameValue = '';
    this.stepNameError = null;
    this._openModal();
  }

  promptNewStep() {
    this.modalMode = 'new-step';
    this.stepNameValue = '';
    this.stepNameError = null;
    this._openModal();
  }

  continueInStep(stepName: string) {
    this.currentStepName = stepName;
    this.isPaused = false;
    this.recordingResumed.emit(stepName);
  }

  private _openModal() {
    this.showStepModal = true;
    this.modalOpenChange.emit(true);
    this.loadDbStepNames();
    setTimeout(() => this.stepNameInputRef.nativeElement.focus(), 100);
  }

  confirmStepName() {
    if (!this.isStepNameValid) return;
    const name = this.stepNameValue.trim();
    this.currentStepName = name;
    this.showStepModal = false;
    this.stepNameValue = '';
    this.stepNameError = null;
    this.isPaused = false;
    this.modalOpenChange.emit(false);

    if (this.modalMode === 'new-step') {
      this.newStepStarted.emit(name);
    } else {
      this.recordingStarted.emit(name);
    }
  }

  cancelStepModal() {
    this.showStepModal = false;
    this.stepNameValue = '';
    this.stepNameError = null;
    this.modalOpenChange.emit(false);
  }

  // ── Recording controls ────────────────────────────────────────────────────────

  pauseRecording() {
    this.isPaused = true;
    this.recordingPaused.emit();
  }

  resumeRecording() {
    this.isPaused = false;
    this.recordingResumed.emit(this.currentStepName);
  }

  stopRecording() {
    this.isPaused = false;
    this.currentStepName = '';
    this.recordingStopped.emit();
  }

  // ── Utility ───────────────────────────────────────────────────────────────────

  getModeIcon(mode: GestureMode | string): string {
    const icons: Record<string, string> = {
      [Literals.TAP]: '☝️',
      [Literals.DOUBLE_TAP]: '✌️',
      [Literals.LONG_PRESS]: '✋',
      [Literals.SWIPE]: '👆',
      [Literals.TYPE]: '⌨️',
      [Literals.ZOOM_IN]: '🔍+',
      [Literals.ZOOM_OUT]: '🔍−',
      [Literals.TWO_FINGER_SWIPE]: '✌️',
      [Literals.THREE_FINGER_SWIPE]: '🤟'
    };
    return icons[mode] || '?';
  }

  getModeLabel(mode: GestureMode): string {
    const labels: Record<string, string> = {
      [Literals.ZOOM_IN]: 'zoom in',
      [Literals.ZOOM_OUT]: 'zoom out',
      [Literals.TWO_FINGER_SWIPE]: '2-finger swipe',
      [Literals.THREE_FINGER_SWIPE]: '3-finger swipe'
    };
    return labels[mode] || mode;
  }
}
